import React, { useState } from 'react';
import { AdminPageHeader } from '../../components/admin/guidance';
import { HANDBOOK_SECTIONS } from '../../content/adminPlatformHandbook';

const toneClass = {
  info: 'border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-100',
  warn: 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
};

const Block = ({ block }) => {
  if (block.type === 'h') {
    return (
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 pt-2">{block.text}</h3>
    );
  }
  if (block.type === 'p') {
    return <p className="text-sm leading-6 text-gray-700 dark:text-gray-300">{block.text}</p>;
  }
  if (block.type === 'note') {
    return (
      <p className={`rounded-xl border px-4 py-3 text-sm leading-6 ${toneClass[block.tone] || toneClass.info}`}>
        {block.text}
      </p>
    );
  }
  if (block.type === 'ul') {
    return (
      <ul className="list-disc pl-5 space-y-2 text-sm leading-6 text-gray-700 dark:text-gray-300">
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === 'table') {
    return (
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/80">
            <tr>
              {block.headers.map((header) => (
                <th
                  key={header}
                  className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => (
              <tr key={row.join('|')} className="border-t border-gray-100 dark:border-gray-800">
                {row.map((cell) => (
                  <td key={cell} className="px-3 py-2 align-top text-gray-700 dark:text-gray-300">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

const AdminPlatformHandbook = () => {
  const [activeId, setActiveId] = useState(HANDBOOK_SECTIONS[0].id);

  const jump = (id) => {
    setActiveId(id);
    const node = document.getElementById(id);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Platform handbook"
        subtitle="How clients, vendors, and admin share one marketplace: markets, tax, and how vendors are paid — including countries where Stripe Connect is not available."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[16rem_minmax(0,1fr)] gap-6 items-start">
        <nav className="lg:sticky lg:top-4 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3">
          <p className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Contents
          </p>
          <ul className="space-y-0.5">
            {HANDBOOK_SECTIONS.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => jump(section.id)}
                  className={`w-full text-left rounded-lg px-2 py-1.5 text-sm ${
                    activeId === section.id
                      ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-5">
          {HANDBOOK_SECTIONS.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className="scroll-mt-4 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 sm:p-6 space-y-3"
            >
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{section.title}</h2>
              {section.summary ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{section.summary}</p>
              ) : null}
              {section.blocks.map((block, index) => (
                <Block key={`${section.id}-${index}`} block={block} />
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPlatformHandbook;
